import { useEffect, useState } from 'react';
import { EditIcon } from '../../../../Icons/Edit/edit';
import { PricingService } from '../../pricing.service';
import {
  QnA,
  VIEW_TYPE,
  getChildQuestionId,
  getHighestPriorityObject,
  transformSelectedAnswersToObject,
} from './data';
import { BasicForm } from './sections/basic-form';
import { PlanView } from './sections/plan-view/PlanView';
import { QnASection } from './sections/qna-section';
import { QUESTION, QUESTIONAIRE, QuestionAnswer } from './types';

export const Questionaire: React.FC<QUESTIONAIRE> = ({
  leadDetails,
  qnaData = QnA,
}) => {
  const INITIAL_STEP_COUNT = 1;
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [selectedAnswers, setSelectedAnswers] = useState<{
    [key: string]: string[];
  }>({});
  const [planData, setPlanData] = useState([]);
  const [viewType, setViewType] = useState(VIEW_TYPE.questionAnswer);
  const [currentStep, setCurrentStep] = useState(INITIAL_STEP_COUNT);
  const [sideBarData, setSideBarData] = useState<QuestionAnswer>({});
  const selectedOptions = transformSelectedAnswersToObject(selectedAnswers);

  const getPlanDetails = async () => {
    const data = await PricingService.getQuestionaireAndPlanDetails(
      selectedOptions
    );
    const plans = data?.planModel || [];
    setPlanData(plans);
    return plans;
  };

  useEffect(() => {
    getPlanDetails();
  }, [viewType]);

  const currentQuestion = qnaData.questions[currentQuestionIndex];

  if (!currentQuestion) return <></>;

  const handleAnswerSelect = (
    questionId: string,
    answerId: string,
    isSingleSelect: boolean
  ) => {
    setSelectedAnswers((prevSelectedAnswers) => {
      const id = getChildQuestionId(answerId, currentQuestion.answers);

      //delete the key if it already exists
      if (id && prevSelectedAnswers[id]) delete prevSelectedAnswers[id];

      const prevSelected = isSingleSelect
        ? []
        : prevSelectedAnswers[questionId] || [];

      const isSelected = prevSelected?.includes(answerId);
      const updatedSelected = isSelected
        ? prevSelected.filter((id) => id !== answerId) // Deselect the answer if already selected
        : [...prevSelected, answerId]; // Select the answer if not already selected

      return { ...prevSelectedAnswers, [questionId]: updatedSelected };
    });
  };

  const handleNext = () => {
    const highestPrioriryAnswer = getHighestPriorityObject(
      selectedAnswers[currentQuestion.id],
      currentQuestion.answers
    )?.highestPriorityOption;

    const sortedSelectedOptions = getHighestPriorityObject(
      selectedAnswers[currentQuestion.id],
      currentQuestion.answers
    )?.selectedOptionsInPriority;

    const answers = sortedSelectedOptions?.map((option) => option.title);

    const QuestionAnswer = {
      [currentQuestion.questionId]: {
        question: currentQuestion.question,
        answer: answers?.map((answer) => answer).join(', ') || '',
      },
    };
    setSideBarData((prevData) => {
      return { ...prevData, ...QuestionAnswer };
    });

    setViewType(highestPrioriryAnswer.viewType);

    if (highestPrioriryAnswer?.viewType === VIEW_TYPE.questionAnswer) {
      setCurrentStep((prevStep) => prevStep + 1);

      const nextQuestionIndex = qnaData.questions.findIndex(
        (question) => question.id === highestPrioriryAnswer.nextQuestionId.id
      );
      if (nextQuestionIndex !== -1) {
        setCurrentQuestionIndex(nextQuestionIndex);
        return;
      }
    }
  };

  const handleBack = () => {
    setCurrentStep(INITIAL_STEP_COUNT);
    setCurrentQuestionIndex(0);
    setViewType(VIEW_TYPE.questionAnswer);
    setSideBarData({});
  };

  switch (viewType) {
    case VIEW_TYPE.questionAnswer:
      return (
        <div className="p-12 md:p-5 sm:p-5 sm:pt-9" data-testid="qna-view">
          <QnASection
            currentQuestion={currentQuestion as QUESTION}
            selectedAnswers={selectedAnswers}
            handleAnswerSelect={handleAnswerSelect}
            handleBack={handleBack}
            handleNext={handleNext}
            currentQuestionIndex={currentQuestionIndex}
            currentStep={currentStep}
          />
        </div>
      );
    case VIEW_TYPE.showPlan:
      return (
        <div data-testid="plan-view">
          <PlanView
            planData={planData}
            handleBack={handleBack}
            changeView={setViewType}
            selectedAnswers={
              selectedAnswers[currentQuestion.id] as unknown as Record<
                string,
                string[]
              >
            }
            leadDetails={leadDetails}
            sideBarData={sideBarData}
          />
        </div>
      );
    default:
      return (
        <div
          className="p-12 relative h-[90vh] md:h-[80vh] sm:h-[84vh] md:p-5 sm:p-5 sm:pt-9"
          data-testid="form-view"
        >
          <div className="w-5/12 mx-auto sm:w-full">
            <div className="flex items-center justify-between gap-2 mb-9">
              <div className="text-s-32 sm:text-s-24 font-semibold">
                Contact us
              </div>
              <div
                onClick={handleBack}
                data-label="You can change recommendation"
                className="flex items-center justify-start gap-1 text-blue-350 text-s-20 cursor-pointer sm:text-s-14"
              >
                <EditIcon /> Edit Details
              </div>
            </div>

            <BasicForm
              changeView={setViewType}
              selectedAnswers={
                selectedAnswers[currentQuestion.id] as unknown as Record<
                  string,
                  string[]
                >
              }
              leadDetails={leadDetails}
            />
          </div>
        </div>
      );
  }
};
